import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { to12h } from '../../utils/time';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/clients', { params: { search } });
      setClients(res.data);
    } catch { setError('Error cargando clientes'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async (clientId) => {
    setHistLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}/appointments`);
      setHistory(res.data);
    } catch { setHistory([]); }
    finally { setHistLoading(false); }
  };

  const selectClient = (c) => {
    setSelected(c);
    loadHistory(c.id);
  };

  const openNew = () => {
    setForm({ name: '', phone: '', email: '', notes: '' });
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setForm({ name: c.name, phone: c.phone, email: c.email || '', notes: c.notes || '' });
    setEditId(c.id);
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, form);
      } else {
        await api.post('/clients', form);
      }
      setShowModal(false);
      load();
      if (selected && selected.id === editId) {
        const res = await api.get(`/clients/${editId}`);
        setSelected(res.data);
      }
    } catch { setError('Error guardando cliente'); }
    finally { setSaving(false); }
  };

  const statusColor = (s) => {
    const map = { confirmed: 'success', attended: 'success', cancelled: 'danger', not_attended: 'danger', rescheduled: 'warning' };
    return map[s] || 'secondary';
  };

  const statusLabel = (s) => {
    const map = { confirmed: 'Confirmada', attended: 'Atendida', cancelled: 'Cancelada', not_attended: 'No asistió', rescheduled: 'Reagendada' };
    return map[s] || s;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Gestión de clientes registrados</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nuevo Cliente</button>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.5fr' : '1fr', gap: '1.5rem' }}>
        {/* Lista */}
        <div className="card">
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
            <input
              className="form-input"
              placeholder="Buscar por nombre o teléfono..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : clients.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No hay clientes registrados</div>
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {clients.map(c => (
                <div
                  key={c.id}
                  onClick={() => selectClient(c)}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    background: selected?.id === c.id ? 'rgba(201,168,76,0.08)' : 'transparent',
                    borderLeft: selected?.id === c.id ? '3px solid var(--color-gold)' : '3px solid transparent',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{c.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                        📞 {c.phone} {c.email && `· ${c.email}`}
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                      onClick={e => { e.stopPropagation(); openEdit(c); }}
                    >Editar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detalle */}
        {selected && (
          <div className="card">
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--color-text)' }}>{selected.name}</h3>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                  {selected.phone} {selected.email && `· ${selected.email}`}
                </div>
                {selected.notes && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--color-surface)', borderRadius: '6px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                    {selected.notes}
                  </div>
                )}
              </div>
              <button className="btn btn-ghost" style={{ fontSize: '1.2rem', padding: '4px 8px' }} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: 'var(--color-gold)' }}>Historial de Citas</h4>
              {histLoading ? (
                <p style={{ color: 'var(--color-text-muted)' }}>Cargando historial...</p>
              ) : history.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)' }}>Sin citas registradas</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '50vh', overflowY: 'auto' }}>
                  {history.map(a => (
                    <div key={a.id} style={{ padding: '0.75rem 1rem', background: 'var(--color-surface)', borderRadius: '8px', borderLeft: '3px solid var(--color-gold)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{a.service_name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                            {new Date(a.appointment_date).toLocaleDateString('es-CO')} · {to12h(a.appointment_time)} · {a.worker_name}
                          </div>
                        </div>
                        <span className={`badge badge-${statusColor(a.status)}`}>{statusLabel(a.status)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editId ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono / Celular *</label>
                <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Observaciones</label>
                <textarea className="form-input" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
